/**
 * ==========================================================================
 *  THE PANE, AND THE PUNCH THAT TAKES IT OUT OF THE FRAME
 * ==========================================================================
 *  There is glass in Einon pirtti's south window and the run goes through it.
 *  It does not go through it by *arriving*: the pane is a wall until Kimmo
 *  puts a fist through it, and putting a fist through it costs a press of F
 *  and the next `GLASS_PAIN.duration` seconds of his speed.
 *
 *  ------------------------------------------------------------------------
 *   IT IS A COLLIDER NOW, AND THAT IS THE WHOLE CHANGE
 *  ------------------------------------------------------------------------
 *  It used to be a sheet of nothing that shattered on the frame a body
 *  crossed its plane - which meant the pane could not refuse anybody, and the
 *  press was decoration. A window you have to break is a window that has to be
 *  able to stop you, so the glass is a box in `CollisionWorld` like the sill
 *  and the header either side of it, and `interact()` is what takes it out.
 *
 *  It is `Door`'s trick with one half of it taken out: the collider is handed
 *  in and the class owns what happens to it, but where a door *moves* its
 *  boxes round with the leaf, glass simply stops existing. It does not come
 *  back and it does not swing, so the box goes on the same step the fist
 *  lands, and Kimmo can be through the frame on the next one.
 *
 *  The pane's box and `GLASS_PAIN` are both hashed into
 *  `simulationFingerprint()`: where the glass is decides where a runner is
 *  stopped, and what it costs decides when he arrives. See `IkkunaSpec` and
 *  src/config/CLAUDE.md.
 *
 *  ------------------------------------------------------------------------
 *   WHERE THE FIST LANDS
 *  ------------------------------------------------------------------------
 *  `impact` is where the look ray meets the pane, in the pane's own fractions,
 *  and the shards fly from there - so a punch aimed low throws glass from low
 *  down. It used to be measured off the chest of a body already halfway
 *  through the hole, which was the only thing available when crossing was what
 *  broke it.
 *
 *  ------------------------------------------------------------------------
 *   NO THREE.JS
 *  ------------------------------------------------------------------------
 *  Same split as Door and Kamiina. This owns when the glass broke and where it
 *  was hit; `World` reads both back out and does the shards, and `Game` reads
 *  the same step and throws the fist across the screen. It can be stepped ten
 *  thousand times in a test with no canvas anywhere near it, and it goes into
 *  the Lambda bundle with the rest of the verifier.
 * ==========================================================================
 */

import { INTERACT } from '../config/gameplay';
import { boxCenter, type IkkunaSpec } from './Level';
import type { Collider } from './CollisionWorld';
import { isLookingAt, type InteractContext, type Interactable } from './Interactable';
import type { Vec3 } from '../utils/math';

/** Where on the pane the fist landed, as fractions of it. */
export interface PaneImpact {
  /** 0 at the pane's `minX` edge, 1 at its `maxX` edge. Clamped. */
  u: number;
  /** 0 at the sill end, 1 at the header end. Clamped. */
  v: number;
}

export class Ikkuna implements Interactable {
  readonly id = 'pirtin-ikkuna';
  readonly prompt = 'RIKO IKKUNA';

  private brokenAtTime: number | null = null;
  private impactPoint: PaneImpact = { u: 0.5, v: 0.5 };
  private readonly center: Vec3;

  constructor(
    private readonly spec: IkkunaSpec,
    private readonly collider: Collider,
  ) {
    this.center = boxCenter(spec.pane);
  }

  /** Simulation time the glass went, or null while it is still in the frame. */
  get brokenAt(): number | null {
    return this.brokenAtTime;
  }

  get isBroken(): boolean {
    return this.brokenAtTime !== null;
  }

  /** Where the pane was hit. Meaningless until `isBroken`; the shards read it. */
  get impact(): PaneImpact {
    return this.impactPoint;
  }

  focusPoint(): Vec3 {
    return this.center;
  }

  /**
   * A doorknob's reach and a doorknob's cone, because that is what this is:
   * something you walk up to and put a hand through. Unlike the kamiina there
   * is no case for widening either - nobody breaks a window at 30 km/h going
   * sideways, and the whole cost of the window line is that you have to come
   * to the glass and deal with it.
   */
  canInteract(context: InteractContext): boolean {
    if (this.spec.boarded || this.brokenAtTime !== null) return false;
    return isLookingAt(context, this.center, INTERACT.maxDistance, INTERACT.minLookDot);
  }

  interact(context: InteractContext): void {
    if (this.spec.boarded || this.brokenAtTime !== null) return;
    this.brokenAtTime = context.simulationTime;
    this.impactPoint = this.aimPoint(context);
    // The hole opens on this step, not on the next one: a runner who punched
    // and kept running must not be stopped by glass that is already on the
    // grass. `RunSimulation` steps the player after the interaction block.
    this.collider.enabled = false;
  }

  reset(): void {
    this.brokenAtTime = null;
    this.impactPoint = { u: 0.5, v: 0.5 };
    this.collider.enabled = true;
  }

  /**
   * Where the look ray crosses the pane's plane, in the pane's own fractions.
   *
   * Clamped rather than rejected. `canInteract` has already decided the player
   * is close enough and pointed roughly enough at the glass, and "roughly" is
   * a 65 degree cone - so an aim that lands past the edge of the pane is a
   * normal punch by somebody standing off to one side, not an error. Falls
   * back to the middle when the ray runs parallel to the glass, which is the
   * one case that has no crossing at all.
   */
  private aimPoint(context: InteractContext): PaneImpact {
    const pane = this.spec.pane;
    const plane = (pane.minZ + pane.maxZ) / 2;
    const dz = context.look.z;
    if (Math.abs(dz) < 1e-6) return { u: 0.5, v: 0.5 };
    const t = (plane - context.eye.z) / dz;
    return {
      u: fraction(context.eye.x + context.look.x * t, pane.minX, pane.maxX),
      v: fraction(context.eye.y + context.look.y * t, pane.minY, pane.maxY),
    };
  }
}

function fraction(value: number, from: number, to: number): number {
  return Math.min(1, Math.max(0, (value - from) / (to - from)));
}
